import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, FormControl, InputGroup, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  confirmQboPurchaseOrderMatch,
  getQboPurchaseOrderMatch,
  removeQboPurchaseOrderMatch,
  searchQboPurchaseOrders,
} from "src/docuclipper/api";
import { createAlert } from "src/redux/reducers/Alerts";
import { useDispatch } from "react-redux";

interface Props {
  integrationId: number;
  transactionId: number;
  invoiceTotal: number;
  onMatchesUpdated?: () => void;
}

const formatCurrency = (value?: number | null) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
};

const statusMeta = (status?: string) => {
  if (status === "matched") {
    return { icon: "check-circle", variant: "success", label: "Matched" };
  }
  if (status === "partially_matched") {
    return { icon: "exclamation-triangle", variant: "warning", label: "Partial match" };
  }
  return { icon: "times-circle", variant: "secondary", label: "No PO matched" };
};

export const InvoicePurchaseOrderMatchSection: React.FC<Props> = ({
  integrationId,
  transactionId,
  invoiceTotal,
  onMatchesUpdated,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [matchData, setMatchData] = useState<any>(null);

  const loadMatch = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getQboPurchaseOrderMatch(integrationId, transactionId);
      setMatchData(response);
      setSearchResults(response?.candidates || []);
    } catch (err) {
      dispatch(
        createAlert({
          message: (err as Error)?.message || "Failed to load purchase order match. Please try again.",
          type: "error",
          timeout: 5000,
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, integrationId, transactionId]);

  useEffect(() => {
    if (integrationId && transactionId) {
      loadMatch();
    }
  }, [integrationId, transactionId, loadMatch]);

  const selectedMatches = Array.isArray(matchData?.selectedMatches)
    ? matchData.selectedMatches
    : [];
  const selectedMatch = matchData?.selectedMatch || null;
  const hasMatch = Boolean(matchData?.hasMatch);
  const topMatch = hasMatch ? matchData?.topMatch || null : null;
  const selectedIsActive =
    Boolean(selectedMatch?.poExternalId) &&
    !selectedMatch?.removedAt &&
    selectedMatch?.matchStatus !== "unmatched";
  // Prefer fresh server-calculated topMatch to avoid stale metadata display.
  const activeMatch = hasMatch
    ? selectedMatches[0] || topMatch || (selectedIsActive ? selectedMatch : null)
    : null;
  const hasDisplayedMatch = Boolean(
    activeMatch?.purchaseOrderId || activeMatch?.poExternalId || activeMatch?.poNumber
  );
  const currentStatus =
    selectedIsActive
      ? selectedMatch?.matchStatus && selectedMatch.matchStatus !== "unmatched"
        ? selectedMatch.matchStatus
        : "partially_matched"
      : hasDisplayedMatch
      ? activeMatch?.status && activeMatch.status !== "unmatched"
        ? activeMatch.status
        : "partially_matched"
      : "unmatched";
  const status = statusMeta(currentStatus);
  const confidence = Math.round(Number(matchData?.confidenceScore || 0) * 100);
  const discrepancySummary: string[] = hasMatch
    ? activeMatch?.discrepancySummary || matchData?.discrepancySummary || []
    : [];
  const uiActions = Array.isArray(matchData?.ui?.actions) ? matchData.ui.actions : [];
  const isActionVisible = (actionId: string, defaultVisible = false) => {
    const action = uiActions.find((a: any) => a?.id === actionId);
    return action ? Boolean(action.visible) : defaultVisible;
  };
  const getActionLabel = (actionId: string, fallback: string) => {
    const action = uiActions.find((a: any) => a?.id === actionId);
    return action?.label || fallback;
  };
  const primarySearchActionId = isActionVisible("change_po", false) ? "change_po" : "find_po";
  const showSearchAction = isActionVisible(primarySearchActionId, true);
  const searchDebounceMs = Number(matchData?.ui?.search?.debounceMs || 300);
  const searchPlaceholder = String(matchData?.ui?.search?.placeholder || "Search by PO number or vendor");

  const amountDiff = useMemo(() => {
    if (!activeMatch) return null;
    const delta = invoiceTotal - Number(activeMatch.total || 0);
    return {
      delta,
      label: delta === 0 ? "Exact total match" : `${delta > 0 ? "+" : "-"}${formatCurrency(Math.abs(delta))}`,
    };
  }, [activeMatch, invoiceTotal]);

  const quickbooksPoUrl = useMemo(() => {
    const poId = activeMatch?.purchaseOrderId;
    if (!poId) return null;
    const baseUrl =
      String(matchData?.quickbooksBaseUrl || "").trim() || "https://app.qbo.intuit.com";
    return `${baseUrl}/app/purchaseorder?txnId=${encodeURIComponent(String(poId))}`;
  }, [activeMatch?.purchaseOrderId, matchData?.quickbooksBaseUrl]);

  const handleConfirm = async (poId: string, acceptWithDifferences = false) => {
    setSaving(true);
    try {
      await confirmQboPurchaseOrderMatch({
        integrationId,
        transactionId,
        purchaseOrderId: poId,
        acceptWithDifferences,
      });
      dispatch(
        createAlert({
          message: "Purchase order match updated",
          type: "success",
          timeout: 3000,
        })
      );
      setShowSearch(false);
      await loadMatch();
      onMatchesUpdated?.();
    } catch (err) {
      dispatch(
        createAlert({
          message: (err as Error)?.message || "Failed to confirm PO match. Please try again.",
          type: "error",
          timeout: 5000,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (poExternalId?: string) => {
    setSaving(true);
    try {
      await removeQboPurchaseOrderMatch(integrationId, transactionId, poExternalId);
      dispatch(
        createAlert({
          message: "Purchase order match removed",
          type: "success",
          timeout: 3000,
        })
      );
      await loadMatch();
      onMatchesUpdated?.();
    } catch (err) {
      dispatch(
        createAlert({
          message: (err as Error)?.message || "Failed to remove PO match. Please try again.",
          type: "error",
          timeout: 5000,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = useCallback(
    async (term: string) => {
      setSearching(true);
      try {
        const response = term.trim()
          ? await searchQboPurchaseOrders(integrationId, transactionId, term.trim())
          : await getQboPurchaseOrderMatch(integrationId, transactionId);
        setSearchResults(response?.candidates || []);
      } catch (err) {
        dispatch(
          createAlert({
            message: (err as Error)?.message || "Failed to search purchase orders. Please try again.",
            type: "error",
            timeout: 5000,
          })
        );
      } finally {
        setSearching(false);
      }
    },
    [dispatch, integrationId, transactionId]
  );

  useEffect(() => {
    if (!showSearch) return;
    const timer = window.setTimeout(() => {
      handleSearch(searchTerm);
    }, searchDebounceMs);
    return () => window.clearTimeout(timer);
  }, [showSearch, searchTerm, searchDebounceMs, handleSearch]);

  useEffect(() => {
    if (!isDrawerOpen) return;
    if (!hasMatch) {
      setShowSearch(true);
    }
  }, [isDrawerOpen, hasMatch]);

  if (loading && !matchData) {
    return (
      <div className="d-flex align-items-center">
        <Spinner animation="border" size="sm" className="mr-2" />
        <small className="text-muted">Loading purchase order match...</small>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div>
          <Badge variant={status.variant} className="mr-2">
            <FontAwesomeIcon icon={status.icon as any} className="mr-1" />
            {status.label}
          </Badge>
          {hasDisplayedMatch && activeMatch?.poNumber && (
            <span className="font-weight-bold text-dark">PO-{activeMatch.poNumber}</span>
          )}
          {confidence > 0 && (
            <small className="text-muted ml-2">{confidence}% match</small>
          )}
        </div>
      </div>
      <Button
        variant="outline-primary"
        size="sm"
        className="w-100"
        onClick={() => setIsDrawerOpen(true)}
      >
        <FontAwesomeIcon icon="file-invoice" className="mr-1" />
        Match to Purchase Orders
      </Button>

      {isDrawerOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1049,
          }}
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: isDrawerOpen ? "65%" : "0",
          backgroundColor: "#fff",
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          transition: "width 0.3s ease-in-out",
          zIndex: 1050,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isDrawerOpen && (
          <>
            <div
              style={{
                padding: "1rem 1.5rem",
                borderBottom: "1px solid #dee2e6",
                backgroundColor: "#f8f9fa",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h5 className="mb-0">
                Purchase Order Matching
                {hasDisplayedMatch && activeMatch?.poNumber && (
                  <Badge variant="primary" className="ml-2">
                    PO-{activeMatch.poNumber}
                  </Badge>
                )}
              </h5>
              <div className="d-flex align-items-center">
                {showSearchAction && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={saving}
                    onClick={() => setShowSearch((prev) => !prev)}
                    className="mr-2"
                  >
                    <FontAwesomeIcon icon="search" className="mr-1" />
                    {getActionLabel(primarySearchActionId, hasMatch ? "Change PO" : "Find PO")}
                  </Button>
                )}
                {isActionVisible("remove_match", false) && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={saving}
                    onClick={() => handleRemove()}
                    className="mr-2"
                    title="Remove match"
                    aria-label="Remove match"
                  >
                    <FontAwesomeIcon icon="trash" />
                  </Button>
                )}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setIsDrawerOpen(false)}
                >
                  <FontAwesomeIcon icon="chevron-right" />
                </Button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "1.5rem" }}>
          {selectedMatches.length > 0 ? (
            <div className="mb-3">
              <small className="text-muted d-block mb-2">
                Matched Purchase Orders ({selectedMatches.length})
              </small>
              <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #e9ecef", borderRadius: 6 }}>
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>PO</th>
                      <th>Vendor</th>
                      <th>Total</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {selectedMatches.map((match: any, idx: number) => (
                      <tr key={`${match.poExternalId || match.purchaseOrderId || match.poNumber}-${idx}`}>
                        <td>PO-{match.poNumber || match.purchaseOrderId || "N/A"}</td>
                        <td>{match.vendor || "Unknown"}</td>
                        <td>{formatCurrency(match.total)}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={saving}
                            onClick={() =>
                              handleRemove(String(match.poExternalId || match.purchaseOrderId || ""))
                            }
                            title="Remove this PO match"
                          >
                            <FontAwesomeIcon icon="trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : activeMatch ? (
            <Alert variant={currentStatus === "matched" ? "success" : "warning"} className="mb-3 py-2">
              <div className="d-flex justify-content-between align-items-start">
                <div className="pr-2">
                  <div className="font-weight-bold">Matched to PO-{activeMatch.poNumber}</div>
                  <small className="text-muted d-block">Vendor: {activeMatch.vendor || "Unknown"}</small>
                  <small className="text-muted d-block">Invoice: {formatCurrency(invoiceTotal)}</small>
                  <small className="text-muted d-block">PO: {formatCurrency(activeMatch.total)}</small>
                  {quickbooksPoUrl && (
                    <div className="mt-1">
                      <a href={quickbooksPoUrl} target="_blank" rel="noopener noreferrer">
                        <small>View in QuickBooks</small>
                      </a>
                    </div>
                  )}
                </div>
                {amountDiff && (
                  <Badge variant={amountDiff.delta === 0 ? "success" : "warning"}>{amountDiff.label}</Badge>
                )}
              </div>
            </Alert>
          ) : (
            <Alert variant="secondary" className="mb-3 py-2">
              No matching PO found
            </Alert>
          )}

          {Boolean(hasMatch && matchData?.whyThisMatch) && (
            <div className="mb-2">
              <small className="text-muted">
                <strong>Why this match:</strong> {matchData.whyThisMatch}
              </small>
            </div>
          )}

          {hasMatch && discrepancySummary.length > 0 && (
            <div className="mb-3">
              {discrepancySummary.map((item: string) => (
                <div key={item} className="text-warning">
                  <FontAwesomeIcon icon="exclamation-circle" className="mr-1" />
                  <small>{item}</small>
                </div>
              ))}
            </div>
          )}

          {hasMatch && activeMatch?.lineComparison?.mismatchedLines?.length > 0 && (
            <div className="mb-3">
              <small className="text-muted d-block mb-2">Line item differences</small>
              <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #e9ecef", borderRadius: 6 }}>
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Invoice line</th>
                      <th>PO line</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeMatch.lineComparison.mismatchedLines.map((line: any, idx: number) => (
                      <tr key={`${line.reason}-${idx}`}>
                        <td>{line.invoiceLine?.description || "-"}</td>
                        <td>{line.poLine?.description || "-"}</td>
                        <td>{line.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {showSearch && (
            <div className="p-2 border rounded">
              <InputGroup className="mb-2">
                <FormControl
                  value={searchTerm}
                  placeholder={searchPlaceholder}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searching && (
                  <InputGroup.Append>
                    <Button variant="outline-secondary" disabled>
                      <Spinner animation="border" size="sm" />
                    </Button>
                  </InputGroup.Append>
                )}
              </InputGroup>
              <small className="text-muted d-block mb-2">Type to search...</small>
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {searchResults.map((candidate: any) => (
                  <div
                    key={`${candidate.purchaseOrderId}-${candidate.poNumber}`}
                    className="d-flex justify-content-between align-items-center py-2 border-bottom"
                  >
                    <div>
                      <div className="font-weight-bold">PO-{candidate.poNumber}</div>
                      <small className="text-muted">
                        {formatCurrency(candidate.total)} - {Math.round(Number(candidate.confidenceScore || 0) * 100)}%
                        {" "}match
                        {candidate.vendor ? ` - ${candidate.vendor}` : ""}
                      </small>
                    </div>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={saving}
                      onClick={() => handleConfirm(String(candidate.purchaseOrderId), false)}
                    >
                      Select
                    </Button>
                  </div>
                ))}
                {searchResults.length === 0 && (
                  <small className="text-muted">No candidates found. You can mark this invoice as No PO.</small>
                )}
              </div>
            </div>
          )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default InvoicePurchaseOrderMatchSection;
